import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getMobileSignatureSession, updateMobileSignatureSession } from '../firestoreHelper';

/**
 * Mobile Signature Page - Standalone page for phone signing
 * Users scan QR code from desktop and sign with their finger
 */
function MobileSignaturePage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  
  const canvasRef = useRef(null);

  // Load session data
  useEffect(() => {
    const loadSession = async () => {
      if (!sessionId) {
        setError('Invalid session. Please scan the QR code again.');
        setLoading(false);
        return;
      }
      
      try {
        const sessionData = await getMobileSignatureSession(sessionId);
        
        if (!sessionData) {
          setError('Session not found or expired. Please scan the QR code again.');
          setLoading(false);
          return;
        }
        
        // Check if session is expired
        if (new Date(sessionData.expiresAt) < new Date()) {
          setError('This signing session has expired. Please request a new QR code.');
          setLoading(false);
          return;
        }
        
        // Check if already signed
        if (sessionData.status === 'signed') {
          setSubmitted(true);
        }
        
        setSession(sessionData);
      } catch (err) {
        console.error('Error loading session:', err);
        setError('Failed to load session. Please try again.');
      }
      
      setLoading(false);
    };
    
    loadSession();
  }, [sessionId]);

  // Canvas drawing functions
  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoordinates(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1e3a8a';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    
    setHasDrawn(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setHasDrawn(false);
  };

  const submitSignature = async () => {
    if (!hasDrawn) {
      alert('Please draw your signature first');
      return;
    }
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const signatureData = canvas.toDataURL('image/png');
    console.log('📱 Submitting signature, data length:', signatureData.length);
    
    try {
      await updateMobileSignatureSession(sessionId, signatureData);
      console.log('✅ Signature submitted successfully to Firestore');
      setSubmitted(true);
    } catch (err) {
      console.error('❌ Error submitting signature:', err);
      alert('Failed to submit signature. Please try again.');
    }
  };

  // Styles
  const styles = {
    container: {
      minHeight: '100dvh', // Use dvh for mobile browsers, fallback handled by CSS
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    },
    card: {
      background: 'white',
      borderRadius: '24px',
      padding: '30px 24px',
      maxWidth: '400px',
      width: '100%',
      boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
      textAlign: 'center'
    },
    logo: {
      width: '80px',
      height: '80px',
      background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px',
      fontSize: '40px'
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '8px'
    },
    subtitle: {
      fontSize: '0.95rem',
      color: '#64748b',
      marginBottom: '24px'
    },
    infoBox: {
      background: '#f0f9ff',
      border: '1px solid #bae6fd',
      borderRadius: '12px',
      padding: '14px',
      marginBottom: '20px',
      textAlign: 'left'
    },
    infoLabel: {
      fontSize: '0.75rem',
      color: '#0369a1',
      fontWeight: '600',
      textTransform: 'uppercase',
      marginBottom: '4px'
    },
    infoValue: {
      fontSize: '0.95rem',
      color: '#0c4a6e',
      fontWeight: '500'
    },
    signatureArea: {
      marginBottom: '20px'
    },
    signatureLabel: {
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '10px',
      textAlign: 'left'
    },
    canvasContainer: {
      border: '2px dashed #d1d5db',
      borderRadius: '12px',
      background: '#fafafa',
      padding: '10px',
      marginBottom: '12px'
    },
    canvas: {
      width: '100%',
      height: '150px',
      touchAction: 'none',
      cursor: 'crosshair',
      borderRadius: '8px',
      background: 'white'
    },
    buttonRow: {
      display: 'flex',
      gap: '10px',
      marginTop: '10px'
    },
    clearBtn: {
      flex: 1,
      padding: '14px 20px',
      background: '#fee2e2',
      border: 'none',
      borderRadius: '10px',
      color: '#dc2626',
      fontWeight: '600',
      fontSize: '0.95rem',
      cursor: 'pointer'
    },
    submitBtn: {
      flex: 2,
      padding: '14px 20px',
      background: 'linear-gradient(135deg, #22c55e, #16a34a)',
      border: 'none',
      borderRadius: '10px',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.95rem',
      cursor: 'pointer',
      opacity: hasDrawn ? 1 : 0.5
    },
    hint: {
      fontSize: '0.8rem',
      color: '#94a3b8',
      marginTop: '15px'
    },
    successIcon: {
      width: '100px',
      height: '100px',
      background: 'linear-gradient(135deg, #22c55e, #16a34a)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 24px',
      fontSize: '50px'
    },
    successTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#16a34a',
      marginBottom: '12px'
    },
    successText: {
      fontSize: '1rem',
      color: '#64748b',
      lineHeight: '1.6'
    },
    errorIcon: {
      width: '80px',
      height: '80px',
      background: 'linear-gradient(135deg, #ef4444, #dc2626)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px',
      fontSize: '40px'
    },
    errorTitle: {
      fontSize: '1.3rem',
      fontWeight: '700',
      color: '#dc2626',
      marginBottom: '12px'
    },
    errorText: {
      fontSize: '0.95rem',
      color: '#64748b'
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={{ ...styles.logo, animation: 'pulse 1.5s infinite' }}>
            ✍️
          </div>
          <h1 style={styles.title}>Loading...</h1>
          <p style={styles.subtitle}>Please wait while we load your signing session</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>❌</div>
          <h1 style={styles.errorTitle}>Session Error</h1>
          <p style={styles.errorText}>{error}</p>
        </div>
      </div>
    );
  }

  // Success state (after signing)
  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>✓</div>
          <h1 style={styles.successTitle}>Signature Submitted!</h1>
          <p style={styles.successText}>
            Your signature has been captured successfully. 
            You can close this page and return to the form on your computer - 
            your signature will appear there automatically.
          </p>
        </div>
      </div>
    );
  }

  // Signing interface
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>✍️</div>
        <h1 style={styles.title}>Sign Here</h1>
        <p style={styles.subtitle}>Use your finger to draw your signature below</p>
        
        {session && (
          <div style={styles.infoBox}>
            <div style={{ marginBottom: '10px' }}>
              <p style={styles.infoLabel}>Signing for</p>
              <p style={styles.infoValue}>
                {session.signatureType === 'taxpayer' ? '👤 Taxpayer Signature' : '👥 Spouse Signature'}
              </p>
            </div>
            {(session.signerName || session.taxpayerName || session.spouseName) && (
              <div>
                <p style={styles.infoLabel}>Name</p>
                <p style={styles.infoValue}>
                  {session.signerName || (session.signatureType === 'spouse' ? session.spouseName : session.taxpayerName)}
                </p>
              </div>
            )}
          </div>
        )}
        
        <div style={styles.signatureArea}>
          <p style={styles.signatureLabel}>Draw your signature:</p>
          <div style={{
            ...styles.canvasContainer,
            touchAction: 'none',
            overflow: 'hidden'
          }}>
            <canvas
              ref={canvasRef}
              width={600}
              height={200}
              style={{
                ...styles.canvas,
                touchAction: 'none',
                WebkitUserSelect: 'none',
                userSelect: 'none',
                WebkitTouchCallout: 'none'
              }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={(e) => { e.preventDefault(); startDrawing(e); }}
              onTouchMove={(e) => { e.preventDefault(); draw(e); }}
              onTouchEnd={(e) => { e.preventDefault(); stopDrawing(); }}
            />
          </div>
          
          <div style={styles.buttonRow}>
            <button 
              onClick={clearSignature}
              style={styles.clearBtn}
            >
              🗑️ Clear
            </button>
            <button 
              onClick={submitSignature}
              style={styles.submitBtn}
              disabled={!hasDrawn}
            >
              ✓ Submit Signature
            </button>
          </div>
        </div>
        
        <p style={styles.hint}>
          💡 Sign using your finger on the touchscreen
        </p>
      </div>
      
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}

export default MobileSignaturePage;
